import React, { Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { AuthProvider } from './hooks/useAuth'
import { ToastProvider } from './features/ui/Toast'
import { ErrorBoundary } from './features/ui/ErrorBoundary'
import { TranslationProvider } from './i18n/TranslationProvider'
import PWAUpdateToast from './features/ui/PWAUpdateToast'

// ── Lazy-loaded route-level modules ──────────────────────────────
const LandingPage = React.lazy(() => import('./features/landing/LandingPage'))
const LoginModule = React.lazy(() => import('./features/auth/LoginModule'))
const VerifyAndPayPage = React.lazy(() => import('./features/auth/VerifyAndPayPage'))
const ResetPasswordPage = React.lazy(() => import('./features/auth/ResetPasswordPage'))
const RoleSelectionPage = React.lazy(() => import('./features/auth/RoleSelectionPage'))
const ForgotPasswordPage = React.lazy(() => import('./features/auth/ForgotPasswordPage'))
const TeacherRegistrationModule = React.lazy(() => import('./features/auth/TeacherRegistrationModule'))
const AdminRegistrationModule = React.lazy(() => import('./features/auth/AdminRegistrationModule'))
const EnrollmentFormModule = React.lazy(
  () => import('./features/enrollment/EnrollmentFormModule').then(m => ({ default: m.EnrollmentFormModule }))
)
const PrivacyTerms = React.lazy(() => import('./features/legal/PrivacyTerms'))
const FullScreenLigio = React.lazy(() => import('./features/ligio/FullScreenLigio'))
const FullScreenApure = React.lazy(() => import('./features/apure/FullScreenApure'))
const MetronomePage = React.lazy(() => import('./features/metronome'))
const TunerPage = React.lazy(() => import('./features/tuner/TunerTool'))
const ScaleVizPage = React.lazy(() => import('./features/scale-visualizer/ScaleVisualizer'))
const CircleOfFifthsPage = React.lazy(() => import('./features/circle-of-fifths/CircleOfFifths'))
const BPMTapPage = React.lazy(() => import('./features/bpm-tap/BPMTap'))
const MidiPlayerPage = React.lazy(() => import('./features/midi-player'))
const SheetMusicPage = React.lazy(() => import('./features/sheet-music/SheetMusicViewer'))
const TimerPage = React.lazy(() => import('./features/practice-timer/PracticeTimer'))
const PdfViewerPage = React.lazy(() => import('./features/pdf-viewer/PdfViewer'))
const CallPage = React.lazy(() => import('./features/video-call/CallPage'))
const DashboardRouter = React.lazy(() => import('./dashboards/DashboardRouter'))

/** Minimal skeleton shown while lazy chunks load. */
const PageFallback: React.FC = () => (
  <div className="min-h-screen bg-white flex items-center justify-center">
    <div className="animate-pulse text-gray-500 text-sm">Loading…</div>
  </div>
)

const App: React.FC = () => {
  return (
    <>
    <ErrorBoundary name="Selah App" onRetry={() => window.location.reload()}>
    <AuthProvider>
    <TranslationProvider>
      <ToastProvider>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            {/* Public pages */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginModule />} />
            <Route path="/enroll" element={<EnrollmentFormModule />} />
            <Route path="/legal" element={<PrivacyTerms />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />

            {/* Staff registration — role choice + paths */}
            <Route path="/register/role-select" element={<RoleSelectionPage />} />
            <Route path="/register/teacher" element={<TeacherRegistrationModule />} />
            <Route path="/register/admin" element={<AdminRegistrationModule />} />

            {/* Email verification → payment → enrollment */}
            <Route path="/payments/verify-and-pay/:token/" element={<VerifyAndPayPage />} />
            <Route path="/payments/verify-and-pay/:token" element={<VerifyAndPayPage />} />

            {/* Full-screen Ligio (no dashboard chrome) */}
            <Route path="/ligio" element={<FullScreenLigio />} />

            {/* Full-screen Apure AI tutor */}
            <Route path="/apure" element={<FullScreenApure />} />

            {/* Video call room */}
            <Route path="/call/:callId" element={<CallPage />} />
            {/* Full-viewport tools (no dashboard chrome) */}
            <Route path="/tool/metronome" element={<MetronomePage />} />
            <Route path="/tool/tuner" element={<TunerPage />} />
            <Route path="/tool/scale-visualizer" element={<ScaleVizPage />} />
            <Route path="/tool/circle-of-fifths" element={<CircleOfFifthsPage />} />
            <Route path="/tool/bpm-tap" element={<BPMTapPage />} />
            <Route path="/tool/midi" element={<MidiPlayerPage />} />
            <Route path="/tool/sheet-music" element={<SheetMusicPage />} />
            <Route path="/tool/timer" element={<TimerPage />} />
            <Route path="/tool/pdf-viewer" element={<PdfViewerPage />} />

            {/* Authenticated dashboard */}
            <Route
              path="/dashboard/*"
              element={<DashboardRouter />}
            />
          </Routes>
        </Suspense>
      </ToastProvider>
    </TranslationProvider>
    </AuthProvider>
    </ErrorBoundary>
    <PWAUpdateToast />
    </>
  )
}

export default App
